import React, { useEffect, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import snakeCase from 'lodash/snakeCase';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import {
  updateInsightByProduct,
  contactCyberExpertRequestedEmail,
  sendEmailToDeclineProducts,
} from '../insights.service';
import {
  AffectedProductStatus,
  SAVE_API_ERROR_BODY,
  SAVE_API_ERROR_TITLE,
  type RowState,
  type SpotlightInsight,
} from './spotlightDetails.types';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';
import {
  requestCyberExpertContactEmail,
  submitCustomerSpotlightFeedback,
} from '../../customer-spotlights/customer-spotlight.service';
import { resolveEffectiveStatus } from './resolveEffectiveStatus';

export interface SpotlightProductTableNotifications {
  success: (title: string, body?: React.ReactNode) => void;
  error: (title: string, body?: React.ReactNode) => void;
  warning: (title: string, body?: React.ReactNode) => void;
}

const OPTIMISTIC_REFETCH_DELAY_MS = 3000;
const OPTIMISTIC_RETRY_DELAY_MS = 1000;
const MAX_OPTIMISTIC_REFETCH_ATTEMPTS = 5;

function resolveOptimisticVersion(
  sentVersion: string | null | undefined,
  previous: RowState | undefined
): string {
  if (sentVersion === null) return '';

  return sentVersion ?? previous?.customerVersion ?? '';
}

function shouldHoldOptimisticStatus(
  previous: RowState | undefined,
  serverStatus: AffectedProductStatus,
  maxAttempts: number
): boolean {
  if (previous?.status === undefined) return false;
  if (serverStatus !== previous.baselineStatus) return false;

  const attempts = previous.holdAttempts ?? 0;
  return attempts < maxAttempts;
}

export function useSpotlightProductTable(params: {
  spotlightId: string | undefined;
  spotlight: SpotlightInsight | undefined;
  queryClient: QueryClient;
  notifications: SpotlightProductTableNotifications;
  queryKey: unknown[];
}) {
  const { spotlightId, spotlight, queryClient, notifications, queryKey } =
    params;

  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const isSpotlightV2PgSource = useFeatureIsOn(
    FEATURE_FLAG_KEYS.SPOTLIGHT_V2_PG_SOURCE
  );

  useEffect(() => {
    if (!spotlight) return;

    const products = spotlight.products;
    if (!Array.isArray(products)) return;

    let needsRetry = false;

    setRowStates((prev) =>
      products.reduce((acc, product) => {
        const key = snakeCase(product.name);
        const previous = prev[key];
        const serverStatus = product.status ?? AffectedProductStatus.UNPATCHED;

        // Hold the optimistic row until the server reports something other
        // than the status we saw when the action was taken — capped, so a
        // row can't hold forever if the server keeps settling on the
        // baseline.
        const holdsOptimisticStatus = shouldHoldOptimisticStatus(
          previous,
          serverStatus,
          MAX_OPTIMISTIC_REFETCH_ATTEMPTS
        );

        if (holdsOptimisticStatus) {
          needsRetry = true;
          return {
            ...acc,
            [key]: {
              ...previous,
              holdAttempts: (previous?.holdAttempts ?? 0) + 1,
            },
          };
        }

        // Preserve a version the user is still typing rather than clobbering
        // it with the server value as soon as the row releases.
        if (previous?.isDirty) {
          return { ...acc, [key]: previous };
        }

        return {
          ...acc,
          [key]: { customerVersion: product.customerVersion ?? '' },
        };
      }, {} as Record<string, RowState>)
    );

    // A row is still waiting to release — retry shortly instead of relying
    // solely on a later window-focus/reconnect refetch.
    if (needsRetry) {
      const timer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey });
      }, OPTIMISTIC_RETRY_DELAY_MS);

      return () => clearTimeout(timer);
    }
    // queryClient is a stable singleton and queryKey is intentionally
    // omitted — it's a new array identity on every render, so including it
    // would re-run this effect (and re-arm the retry) on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotlight]);

  const handleVersionChange = (id: string, value: string) => {
    const key = snakeCase(id);
    setRowStates((prev) => ({
      ...prev,
      [key]: { ...prev[key], customerVersion: value, isDirty: true },
    }));
  };

  const saveSpotlightProduct = async (
    productName: string,
    payload: {
      status: AffectedProductStatus;
      customerVersion?: string | null;
      declinedReason?: string | null;
      statusReason?: string | null;
    },
    successTitle: string,
    successBody: string
  ): Promise<boolean> => {
    if (!spotlightId) return false;

    const accountId = spotlight?.accountId;
    if (!accountId) return false;

    try {
      if (isSpotlightV2PgSource) {
        const product = spotlight?.products?.find(
          (p) => p.name === productName
        );

        if (!product) return false;

        await submitCustomerSpotlightFeedback({
          data: {
            spotlightProductDetailsId: product.id,
            status: payload.status,
            ...(payload.status === AffectedProductStatus.DECLINED
              ? { statusReason: payload.declinedReason ?? payload.statusReason }
              : {}),
            customerVersion: payload.customerVersion ?? product.customerVersion,
          },
        });

        const key = snakeCase(productName);
        setRowStates((prev) => ({
          ...prev,
          [key]: {
            customerVersion: resolveOptimisticVersion(
              payload.customerVersion,
              prev[key]
            ),
            status: payload.status,
            baselineStatus: resolveEffectiveStatus(
              prev[key]?.status,
              product.status
            ),
            isDirty: false,
            holdAttempts: 0,
          },
        }));

        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey });
        }, OPTIMISTIC_REFETCH_DELAY_MS);

        notifications.success(successTitle, successBody);
        return true;
      }

      await updateInsightByProduct(spotlightId, {
        data: { accountId, productName, ...payload },
      });

      queryClient.invalidateQueries({
        queryKey,
      });
      notifications.success(successTitle, successBody);
      return true;
    } catch (error) {
      console.error('Error updating insight by product', error);
      notifications.error(SAVE_API_ERROR_TITLE, SAVE_API_ERROR_BODY);
      return false;
    }
  };

  const handlePatch = async (productName: string, customerVersion: string) => {
    if (!customerVersion?.trim()) {
      notifications.warning(
        'Version required',
        `Enter the current version for ${productName} in the field, then click Patch again.`
      );
      return;
    }

    await saveSpotlightProduct(
      productName,
      { status: AffectedProductStatus.PATCHED, customerVersion },
      'Saved as patched',
      `We recorded the current version for "${productName}" product and marked it as patched.`
    );
  };

  const handleNotApplicable = async (productName: string) => {
    await saveSpotlightProduct(
      productName,
      { status: AffectedProductStatus.NOT_APPLICABLE, customerVersion: null },
      'Saved as not applicable',
      `We marked "${productName}" product as not applicable for this insight.`
    );
  };

  const handleUndo = async (productName: string) => {
    await saveSpotlightProduct(
      productName,
      { status: AffectedProductStatus.UNPATCHED, customerVersion: null },
      'Change reverted',
      `"${productName}" product was reset. You can enter a current installed version again.`
    );
  };

  const handleSubmitForReview = async (
    productName: string,
    customerVersion: string
  ) => {
    if (!customerVersion?.trim()) {
      notifications.warning(
        'Version required',
        `Enter the current version for ${productName} in the field, then click Submit For Review again.`
      );
      return;
    }

    await saveSpotlightProduct(
      productName,
      {
        status: AffectedProductStatus.SUBMIT_FOR_REVIEW,
        customerVersion,
      },
      'Submitted for review',
      `We submitted "${productName}" product for review.`
    );
  };

  const handleContactCyberExpert = async (productName: string) => {
    try {
      if (isSpotlightV2PgSource) {
        const product = spotlight?.products?.find(
          (p) => p.name === productName
        );

        if (!product) {
          throw new Error('Product not found');
        }

        await requestCyberExpertContactEmail(spotlightId, product.id);
      } else {
        await contactCyberExpertRequestedEmail(spotlightId, productName);
      }

      notifications.success(
        `We have sent an email to the cyber expert for "${productName}" product. They will contact you soon.`
      );
    } catch (error) {
      console.error('Error contacting cyber expert', error);

      notifications.error(
        'Failed to contact cyber expert',
        'We were unable to send an email to the cyber expert. Please try again later.'
      );
    }
  };

  const handleDecline = async (
    productName: string,
    customerVersion?: string | null,
    declinedReason?: string
  ) => {
    return await saveSpotlightProduct(
      productName,
      {
        status: AffectedProductStatus.DECLINED,
        ...(customerVersion ? { customerVersion } : {}),
        declinedReason,
      },
      'Declined',
      `We declined "${productName}" product for this spotlight.`
    );
  };

  const handleSendEmailToDeclineProducts = async (productNames: string[]) => {
    try {
      await sendEmailToDeclineProducts(spotlightId, {
        data: productNames,
      });

      notifications.success(
        'Email sent to decline products',
        'We have sent an email to the policyholder for the declined products.'
      );
    } catch (error) {
      console.error('Error sending email to decline products', error);
      notifications.error(
        'Failed to send email to decline products',
        'We were unable to send an email to the policyholder. Please try again later.'
      );
    }
  };

  return {
    rowStates,
    handleVersionChange,
    handlePatch,
    handleNotApplicable,
    handleUndo,
    handleSubmitForReview,
    handleContactCyberExpert,
    handleDecline,
    handleSendEmailToDeclineProducts,
  };
}
